// ─── White-Label Branding Config ─────────────────────────────────────────────
// Single source of truth for all brand-specific values.
// All values are read from VITE_BRAND_* environment variables baked in at
// build time — each brand branch carries its own .env.<brand> file.
//
// HOW TO USE:
//   import { branding } from '@/config/branding'   ← in composables/config only
//   const { brandName } = useBranding()             ← in Vue components (preferred)
//
// NEVER call import.meta.env.VITE_BRAND_* directly in components.
// NEVER hardcode brand strings (name, email, URL) in components or views.
// ─────────────────────────────────────────────────────────────────────────────

export const branding = {

  // ── Identity ──────────────────────────────────────────────────────────────
  name:        import.meta.env.VITE_BRAND_NAME        || 'The Change Network',
  tagline:     import.meta.env.VITE_BRAND_TAGLINE      || 'Speak Freely',
  companyName: import.meta.env.VITE_BRAND_COMPANY_NAME || 'Orate Corp',
  website:     import.meta.env.VITE_BRAND_WEBSITE      || 'https://dougjones.com',

  // ── Assets ────────────────────────────────────────────────────────────────
  // Paths are relative to /public — place brand assets in /public/icon/<brand>/
  logo: {
    main:     import.meta.env.VITE_BRAND_LOGO_MAIN      || '/icon/cn_dark.svg',
    icon:     import.meta.env.VITE_BRAND_LOGO_ICON      || '/icon/cn_dark.svg',
    iconDark: import.meta.env.VITE_BRAND_LOGO_ICON_DARK || import.meta.env.VITE_BRAND_LOGO_ICON || '/icon/cn_dark.svg',
    favicon:  import.meta.env.VITE_BRAND_FAVICON        || '/fav.ico',
    ogImage:  import.meta.env.VITE_BRAND_OG_IMAGE       || 'https://changenetwork.dougjones.com/icon/cn_dark.png',
  },

  // ── Contact & support ─────────────────────────────────────────────────────
  contact: {
    supportEmail:  import.meta.env.VITE_SUPPORT_EMAIL       || 'info@dougjones.com',
    brandEmail:    import.meta.env.VITE_SUPPORT_BRAND_EMAIL || 'info@dougjones.com',
    reportEmail:   import.meta.env.VITE_REPORT_EMAIL        || 'info@dougjones.com',
    helpEmail:     import.meta.env.VITE_HELP_EMAIL          || 'info@dougjones.com',
    helpUsername:  import.meta.env.VITE_HELP_USERNAME       || 'dougjoneshelp',
    brandUsername: import.meta.env.VITE_BRAND_USERNAME      || 'dougjones',
  },

  // ── Domains ───────────────────────────────────────────────────────────────
  // Used in Leftbar environment switcher (Leftbar/index.vue:176) and the
  // domain-check in Post/index.vue:2064 to enable brand-specific behavior.
  domains: {
    prod:    import.meta.env.VITE_BRAND_DOMAIN_PROD || 'https://dougjones.com',
    staging: import.meta.env.VITE_BRAND_DOMAIN_STG  || 'https://staging-cn.digicollect.com',
    dev:     import.meta.env.VITE_BRAND_DOMAIN_DEV  || 'https://dev-cn.digicollect.com',
  },

  // ── Beta / waitlist ───────────────────────────────────────────────────────
  // API endpoint called by AboutUs/index.vue, declarationPage.vue, termsOfService.vue
  betaUsersUrl: import.meta.env.VITE_BRAND_BETA_USERS_URL || 'https://dougjones.com/api/beta-users',

  // ── UI Messaging ──────────────────────────────────────────────────────────
  messaging: {
    welcomeTitle:  import.meta.env.VITE_BRAND_WELCOME_TITLE   || 'How is The Change Network different?',
    // Shown on SMS verification screens
    smsConsent:    import.meta.env.VITE_BRAND_SMS_CONSENT     || 'dougjones.com may send me 2FA codes via SMS. Message and data rates may apply.',
    // Appended to system emails / support replies
    teamSignature: import.meta.env.VITE_BRAND_TEAM_SIGNATURE  || '- The Change Network Team',
    // Shown in createPostModal.vue report flow (Post/index.vue:571)
    communityName: import.meta.env.VITE_BRAND_COMMUNITY_NAME  || 'The Change Network',
    // Shown on help page (dougjoneshelp.vue)
    helpPageTitle: import.meta.env.VITE_BRAND_HELP_PAGE_TITLE || 'The Change Network Help',
    // Shown in connectExternalAccountsModal.vue
    externalAccountConsent: import.meta.env.VITE_BRAND_EXTERNAL_ACCOUNT_CONSENT ||
      'I understand this connection allows The Change Network and the platforms I connect to share my data.',
  },

  // ── Legal page content ────────────────────────────────────────────────────
  // Terms of service and privacy pages are heavily branded.
  // Provide full URLs so the brand-specific legal pages can be linked.
  legal: {
    privacyUrl:  import.meta.env.VITE_BRAND_PRIVACY_URL  || 'https://dougjones.com/privacy',
    termsUrl:    import.meta.env.VITE_BRAND_TERMS_URL    || 'https://dougjones.com/terms',
    // Contact used inside legal documents (termsOfService.vue, privacy.vue)
    legalEmail:  import.meta.env.VITE_BRAND_LEGAL_EMAIL  || 'akshay.gupta@dougjones.com',
  },

  // ── Auth integrations ─────────────────────────────────────────────────────
  // Sign-in with Farcaster — used in Header, LandingPage, BaseModal, connectExternalAccountsModal
  siwfLoginUrl:     import.meta.env.VITE_SIWF_LOGIN_URL     || '',
  // Google reCAPTCHA site key — injected in index.html script tag
  recaptchaSiteKey: import.meta.env.VITE_RECAPTCHA_SITE_KEY || '6Le7KLosAAAAAGvYmtE5tu93Xj2Y9278wAA9e1If',

  // ── Theme & fonts ─────────────────────────────────────────────────────────
  // Must match a key in src/config/theme.js
  themePreset: import.meta.env.VITE_THEME_PRESET     || 'default',
  fontFamily:  import.meta.env.VITE_BRAND_FONT_FAMILY || '',

  // ── OG / SEO (index.html) ─────────────────────────────────────────────────
  // index.html cannot read import.meta.env directly, but Vite replaces
  // %VITE_*% tokens in index.html at build time.
  // To wire these up, update index.html to use:
  //   <title>%VITE_BRAND_NAME%</title>
  //   <meta property="og:title" content="%VITE_BRAND_NAME%" />
  //   <meta property="og:image" content="%VITE_BRAND_OG_IMAGE%" />
  //   <meta property="og:url"   content="%VITE_BRAND_WEBSITE%" />
  seo: {
    pageTitle:     import.meta.env.VITE_BRAND_NAME    || 'The Change Network',
    ogTitle:       import.meta.env.VITE_BRAND_NAME    || 'The Change Network',
    ogDescription: import.meta.env.VITE_BRAND_OG_DESCRIPTION ||
      'The social media platform built on the real world principles of identity and reputation and a commitment to democracy and freedom of expression.',
    ogImage:       import.meta.env.VITE_BRAND_OG_IMAGE || 'https://dougjones.com/icon/og_image.jpg',
    ogUrl:         import.meta.env.VITE_BRAND_WEBSITE  || 'https://dougjones.com',
  },
};

// ─── Allen Jackson Branding ───────────────────────────────────────────────────
// Standalone export for direct import when building Allen Jackson specifically.
// In normal use, set VITE_THEME_PRESET=allenjackson and all VITE_BRAND_* vars
// in .env.allenjackson — the main `branding` export above will pick them up.
// ─────────────────────────────────────────────────────────────────────────────
export const allenJacksonBranding = {
  name:        'Allen Jackson',
  tagline:     'Your Voice, Your Community',
  companyName: 'Allen Jackson Corp',
  website:     'https://allenjackson.com',

  logo: {
    main:    '/icon/allenjackson/logo.svg',
    icon:    '/icon/allenjackson/icon.svg',
    favicon: '/icon/allenjackson/fav.ico',
    ogImage: 'https://allenjackson.com/icon/og_image.jpg',
  },

  contact: {
    supportEmail:  'info@allenjackson.com',
    reportEmail:   'report@allenjackson.com',
    helpEmail:     'help@allenjackson.com',
    helpUsername:  'allenjacksonhelp',
    brandUsername: 'allenjackson',
  },

  domains: {
    prod:    'https://allenjackson.com',
    staging: 'https://stg.allenjackson.com',
    dev:     'https://dev.allenjackson.com',
  },

  betaUsersUrl: 'https://allenjackson.com/api/beta-users',

  messaging: {
    welcomeTitle:            'How is Allen Jackson different?',
    smsConsent:              'allenjackson.com may send me 2FA codes via SMS. Message and data rates may apply.',
    teamSignature:           '- Allen Jackson Team',
    communityName:           'Allen Jackson',
    helpPageTitle:           'Allen Jackson Help',
    externalAccountConsent:  'I understand this connection allows Allen Jackson and the platforms I connect to share my data.',
  },

  legal: {
    privacyUrl: 'https://allenjackson.com/privacy',
    termsUrl:   'https://allenjackson.com/terms',
    legalEmail: 'legal@allenjackson.com',
  },

  siwfLoginUrl:     '',
  recaptchaSiteKey: '',
  themePreset:      'allenjackson',
  fontFamily:       '',

  seo: {
    pageTitle:     'Allen Jackson',
    ogTitle:       'Allen Jackson',
    ogDescription: 'The social platform for Allen Jackson — connecting community with purpose.',
    ogImage:       'https://allenjackson.com/icon/og_image.jpg',
    ogUrl:         'https://allenjackson.com',
  },
};
